/**
 * The starter page `wayai page new` writes (wayai-pro/wayai#5333 decision 14): a `page.yaml` and an
 * `index.html` that push takes as written, already following the design guide in
 * `skills/wayai/references/pages.md`:
 *
 * - styled only with the host's tokens and base classes (`@wayai/core/page-design`), mobile-first:
 *   one card per row on a narrow frame, a table once its container is wide enough;
 * - the MCP Apps messages a page sends and hears, posted by hand (no SDK to inline);
 * - the viewer's theme (`data-theme` on `<html>`) and language (`en`, `pt`, `es`), followed live;
 * - loading, empty and error states, the host's refusal codes among the errors;
 * - a read again whenever the host raises `wayai.refresh`.
 *
 * A customer page (`audience: customers`, #5333 row 3.4) is offered in the conversation header
 * (`show_in_conversation: true`, a customer's only way in) and only reads: `customer_writes` is left
 * unset, and its comments name the toolset its list tool comes from: one whose record types declare person
 * entries or `{ shared: true }`, since each call is for the person using the page (wayai-pro/wayai#5473 2b2).
 *
 * The manifest is checked with the platform's own schema before anything is written, so a refusal
 * reads as push's would (`page "<slug>": <field>: <message>`).
 */

import * as yaml from 'js-yaml';
import { PAGE_CUSTOMER_UNAVAILABLE_CODE, PAGE_REVISION_STALE_CODE, pageManifestSchema } from '@wayai/core/contracts/pages';
import { expected } from './expected.js';
import { YAML_DUMP_OPTIONS } from './yaml-writer.js';

/** The MCP Apps protocol version WayAI's host answers `ui/initialize` with. */
export const PAGE_PROTOCOL_VERSION = '2026-01-26';

export interface PageScaffoldInput {
  slug: string;
  /** Defaults to the slug in words: `swim-results` → `Swim results`. */
  title?: string;
  audience: string;
}

export interface PageScaffoldFiles {
  manifestYaml: string;
  html: string;
}

function titleOf(slug: string): string {
  const words = slug.split('-').join(' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** The starter page's two files, or an `expected` refusal naming what the platform would refuse. */
export function scaffoldPage(input: PageScaffoldInput): PageScaffoldFiles {
  const customers = input.audience === 'customers';
  const manifest = {
    slug: input.slug,
    title: input.title ?? titleOf(input.slug),
    audience: input.audience,
    ...(customers ? { show_in_conversation: true } : {}),
    tools: [],
  };
  const parsed = pageManifestSchema.safeParse(manifest);
  if (!parsed.success) {
    const issues = parsed.error.issues.map((i) => (i.path.length > 0 ? `${i.path.join('.')}: ${i.message}` : i.message));
    throw expected(issues.map((issue) => `page "${input.slug}": ${issue}`).join('\n'));
  }
  const { slug: _slug, ...written } = manifest;
  const header = customers
    ? '# The page\'s manifest: pages.md in the WayAI skill has every field, and its Customer Pages section\n' +
      '# what a customer page may reach. Hub Users given access on the Users tab open it from their own\n' +
      '# conversation\'s header; those an account-mode hub adds automatically are admitted but have no way in\n' +
      '# from the apps yet.\n' +
      '# Each call is for the person using it: add the tool it lists to tools, from a Base Toolset connection\n' +
      '# whose record types declare person entries or { shared: true }, so each customer reads only their own\n' +
      '# records and shared ones:\n' +
      '# tools: - { connection: <a Base Toolset connection in hub.yaml>, tool: <its list tool>, notify: none }\n' +
      '# It only reads. customer_writes: true lets each customer run its write tools: on their own records, and on\n' +
      '# every record of a { shared: true } type, which every other customer then sees.\n'
    : '# The page\'s manifest: pages.md in the WayAI skill has every field. Add each tool the page calls to\n' +
      '# tools: - { connection: <a Base Toolset connection in hub.yaml>, tool: <its tool name>, notify: none }\n';
  const manifestYaml = header + yaml.dump(written, YAML_DUMP_OPTIONS);
  return { manifestYaml, html: starterHtml(input.slug, parsed.data.title) };
}

function starterHtml(slug: string, title: string): string {
  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<title>${escapeHtml(title)}</title>
<style>
  /* WayAI's base stylesheet comes first: its tokens (var(--...)) and wayai-* classes are ready to
     use, and every rule written here wins over it. Style with the tokens, never fixed colors. */
  body {
    padding: var(--safe-top, 0px) var(--safe-right, 0px) var(--safe-bottom, 0px) var(--safe-left, 0px);
  }
  .page-header {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: var(--wayai-space-2);
  }
  .items {
    container-type: inline-size;
  }
  .items p {
    margin: 0;
  }
  .item-card {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--wayai-space-1);
  }
  .item-meta {
    font-size: var(--font-text-xs-size);
  }
  /* Narrow first: one card per row. A table once the list's container has room for columns. */
  .item-table {
    display: none;
  }
  @container (min-width: 560px) {
    .item-cards {
      display: none;
    }
    .item-table {
      display: block;
    }
  }
  /* The base classes set display, so hidden needs this to win over them. */
  [hidden] {
    display: none !important;
  }
</style>
</head>
<body>
<main class="wayai-page">
  <header class="page-header">
    <h1 class="wayai-title" id="title">${escapeHtml(title)}</h1>
    <button type="button" class="wayai-button" id="refresh"></button>
  </header>
  <section class="items wayai-stack" id="items" aria-live="polite" aria-busy="true">
    <p class="wayai-empty" id="status"></p>
    <div class="wayai-alert wayai-alert--danger wayai-stack" id="error" role="alert" hidden>
      <p id="error-text"></p>
      <div class="wayai-row">
        <button type="button" class="wayai-button" id="retry"></button>
      </div>
    </div>
    <ul class="wayai-list item-cards" id="cards" hidden></ul>
    <div class="wayai-table-wrap item-table" id="table" hidden>
      <table class="wayai-table">
        <thead><tr><th id="col-name"></th><th id="col-updated"></th></tr></thead>
        <tbody id="rows"></tbody>
      </table>
    </div>
  </section>
</main>
<script>
  // The tool that lists this page's records: one of page.yaml's tools, by its tool name, and the
  // arguments to call it with. Until one is set, the page shows its empty state.
  const LIST_TOOL = '';
  const LIST_ARGUMENTS = {};

  // Every string the viewer reads, in each language the host passes (its context's locale).
  const STRINGS = {
    en: { refresh: 'Refresh', loading: 'Loading…', empty: 'Nothing here yet.', notSetUp: 'This page does not list anything yet.', failed: 'Could not load this list.', retry: 'Try again', stale: 'This page was updated. Reload it to continue.', rateLimited: 'Too many requests. Try again in {seconds} seconds.', denied: 'This page is no longer available to you.', unavailable: 'This page is not available to your account.', notFound: 'This page, or the list it shows, is not available.', offline: 'Could not reach WayAI. Check your connection and try again.', name: 'Name', updated: 'Updated' },
    pt: { refresh: 'Atualizar', loading: 'Carregando…', empty: 'Nada aqui ainda.', notSetUp: 'Esta página ainda não lista nada.', failed: 'Não foi possível carregar esta lista.', retry: 'Tentar de novo', stale: 'Esta página foi atualizada. Recarregue-a para continuar.', rateLimited: 'Muitas solicitações. Tente de novo em {seconds} segundos.', denied: 'Esta página não está mais disponível para você.', unavailable: 'Esta página não está disponível para a sua conta.', notFound: 'Esta página, ou a lista que ela mostra, não está disponível.', offline: 'Não foi possível conectar ao WayAI. Verifique sua conexão e tente de novo.', name: 'Nome', updated: 'Atualizado' },
    es: { refresh: 'Actualizar', loading: 'Cargando…', empty: 'Todavía no hay nada aquí.', notSetUp: 'Esta página todavía no muestra nada.', failed: 'No se pudo cargar esta lista.', retry: 'Reintentar', stale: 'Esta página se actualizó. Vuelve a cargarla para continuar.', rateLimited: 'Demasiadas solicitudes. Inténtalo de nuevo en {seconds} segundos.', denied: 'Esta página ya no está disponible para ti.', unavailable: 'Esta página no está disponible para tu cuenta.', notFound: 'Esta página, o la lista que muestra, no está disponible.', offline: 'No se pudo conectar con WayAI. Revisa tu conexión e inténtalo de nuevo.', name: 'Nombre', updated: 'Actualizado' },
  };

  // What the host told the page (ui/initialize, then each host-context-changed), and what it shows.
  const context = { theme: 'light', locale: 'en', refresh: undefined };
  let view = { kind: 'loading' };
  // A rate-limit wait: no call is started until it ends (one timer per refusal, never a poll).
  let waiting = false;
  // wayai.refresh rose while a read was in flight: read again once it settles.
  let refreshPending = false;

  const $ = (id) => document.getElementById(id);
  const language = () => (STRINGS[context.locale] ? context.locale : 'en');
  function t(key, values = {}) {
    return STRINGS[language()][key].replace(/\\{(\\w+)\\}/g, (_, name) => String(values[name] ?? ''));
  }

  // ── The MCP Apps messages: JSON-RPC 2.0 posted to the host ──
  let nextId = 1;
  const pending = new Map();
  window.addEventListener('message', (event) => {
    if (event.source !== window.parent) return;
    const message = event.data;
    if (!message || message.jsonrpc !== '2.0') return;
    if (message.id !== undefined && pending.has(message.id)) {
      const { resolve, reject } = pending.get(message.id);
      pending.delete(message.id);
      if (message.error) reject(message.error);
      else resolve(message.result);
    } else if (message.method === 'ui/notifications/host-context-changed') {
      applyContext(message.params || {});
    }
  });
  function request(method, params) {
    const id = nextId++;
    return new Promise((resolve, reject) => {
      pending.set(id, { resolve, reject });
      window.parent.postMessage({ jsonrpc: '2.0', id, method, params }, '*');
    });
  }
  function notify(method, params) {
    window.parent.postMessage({ jsonrpc: '2.0', method, params }, '*');
  }
  const callTool = (name, args) => request('tools/call', { name, arguments: args });

  // ── Theme, language and safe area: a change carries only the fields that changed ──
  function applyContext(changed) {
    if (changed.theme) {
      context.theme = changed.theme;
      document.documentElement.setAttribute('data-theme', changed.theme);
    }
    if (changed.locale) context.locale = changed.locale;
    // wayai.refresh rises when what the page shows may have changed: read again (pages.md, Live Refresh).
    // It arrives whole with any change to wayai, so only a new value reads.
    if (changed.wayai && changed.wayai.refresh !== undefined && changed.wayai.refresh !== context.refresh) {
      const known = context.refresh !== undefined;
      context.refresh = changed.wayai.refresh;
      if (known && view.kind === 'loading') refreshPending = true;
      else if (known && !waiting && !(view.kind === 'error' && view.key === 'stale')) load();
    }
    if (changed.safeAreaInsets) {
      for (const side of ['top', 'right', 'bottom', 'left']) {
        document.documentElement.style.setProperty('--safe-' + side, (changed.safeAreaInsets[side] || 0) + 'px');
      }
    }
    render();
  }

  // ── Loading the list ──
  async function load() {
    await read();
    // A rise while it ran is read now, unless the read left the page waiting or stale.
    if (refreshPending) {
      refreshPending = false;
      if (!waiting && !(view.kind === 'error' && view.key === 'stale')) load();
    }
  }

  async function read() {
    view = { kind: 'loading' };
    render();
    if (!LIST_TOOL) {
      view = { kind: 'empty', key: 'notSetUp' };
      return render();
    }
    let result;
    try {
      result = await callTool(LIST_TOOL, LIST_ARGUMENTS);
    } catch {
      view = { kind: 'error', key: 'offline' };
      return render();
    }
    if (result.isError) {
      view = refusal(result);
      if (view.key === 'rateLimited') waitOut(view.values.seconds);
      return render();
    }
    const rows = rowsOf(result);
    view = rows.length === 0 ? { kind: 'empty', key: 'empty' } : { kind: 'list', rows };
    render();
  }

  // A refusal of the call (structuredContent.code) or of the tool itself (its own text). A NOT_FOUND
  // keeps the host's text beside its own: "Page not found" (no longer offered to this viewer) or
  // "Page tool not found" (LIST_TOOL is not in page.yaml's tools).
  function refusal(result) {
    const code = result.structuredContent && result.structuredContent.code;
    if (code === '${PAGE_REVISION_STALE_CODE}') return { kind: 'error', key: 'stale' };
    if (code === 'RATE_LIMIT_EXCEEDED') {
      const seconds = Math.max(1, Number(result.structuredContent.retry_after_seconds) || 60);
      return { kind: 'error', key: 'rateLimited', values: { seconds } };
    }
    if (code === 'AUTHORIZATION_ERROR') return { kind: 'error', key: 'denied' };
    if (code === '${PAGE_CUSTOMER_UNAVAILABLE_CODE}') return { kind: 'error', key: 'unavailable' };
    const text = (result.content || []).find((part) => part.type === 'text');
    if (code === 'NOT_FOUND') return { kind: 'error', key: 'notFound', detail: text && text.text };
    return text && text.text ? { kind: 'error', text: text.text } : { kind: 'error', key: 'failed' };
  }

  function waitOut(seconds) {
    waiting = true;
    setTimeout(() => {
      waiting = false;
      render();
    }, seconds * 1000);
  }

  // A toolset's list answers JSON text: { "data": [ ...rows ] }. Shape each row to your records.
  function rowsOf(result) {
    const text = (result.content || []).find((part) => part.type === 'text');
    try {
      const body = JSON.parse(text ? text.text : '{}');
      return Array.isArray(body.data) ? body.data : [];
    } catch {
      return [];
    }
  }
  const nameOf = (row) => String((row.data && (row.data.name || row.data.title)) || row.external_id || row.id || '');
  const updatedOf = (row) => (row.updated_at ? new Date(row.updated_at).toLocaleString(language()) : '');

  // ── Rendering: from view and context alone, so a language switch redraws everything ──
  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text; // never innerHTML: rows hold what people wrote
    return node;
  }
  function render() {
    document.documentElement.lang = language();
    $('refresh').textContent = t('refresh');
    // Stale: no call can succeed until the viewer reloads the page, which the host offers.
    const stale = view.kind === 'error' && view.key === 'stale';
    $('refresh').disabled = view.kind === 'loading' || waiting || stale;
    $('retry').textContent = t('retry');
    $('retry').disabled = waiting;
    $('retry').hidden = stale;
    $('col-name').textContent = t('name');
    $('col-updated').textContent = t('updated');
    $('items').setAttribute('aria-busy', String(view.kind === 'loading'));

    const showsStatus = view.kind === 'loading' || view.kind === 'empty';
    $('status').hidden = !showsStatus;
    $('status').textContent = view.kind === 'loading' ? t('loading') : view.kind === 'empty' ? t(view.key) : '';
    $('error').hidden = view.kind !== 'error';
    $('error-text').textContent = view.kind !== 'error' ? ''
      : view.text || t(view.key, view.values) + (view.detail ? ' (' + view.detail + ')' : '');

    const rows = view.kind === 'list' ? view.rows : [];
    $('cards').hidden = rows.length === 0;
    $('table').hidden = rows.length === 0;
    $('cards').replaceChildren(...rows.map((row) => {
      const item = element('li', 'wayai-list-item item-card');
      item.append(element('span', '', nameOf(row)));
      if (updatedOf(row)) item.append(element('span', 'wayai-muted item-meta', updatedOf(row)));
      return item;
    }));
    $('rows').replaceChildren(...rows.map((row) => {
      const tr = element('tr');
      tr.append(element('td', '', nameOf(row)), element('td', 'wayai-muted', updatedOf(row)));
      return tr;
    }));
  }

  $('refresh').addEventListener('click', load);
  $('retry').addEventListener('click', load);

  async function main() {
    render();
    const answer = await request('ui/initialize', {
      protocolVersion: '${PAGE_PROTOCOL_VERSION}',
      appInfo: { name: '${slug}', version: '1.0.0' },
      appCapabilities: {},
    });
    applyContext(answer.hostContext || {});
    notify('ui/notifications/initialized', {});
    load();
  }
  main();
</script>
</body>
</html>
`;
}
